/**
 * ClienteBlocoEditavel — F2 (CLIENTES-FIX): edição por bloco na ficha do cliente.
 *
 * Padrão ÚNICO dos 3 blocos da aba Identificação (Dados principais / Fiscal e
 * inscrições / Dados bancários): botão "Editar" no cabeçalho do card → os
 * campos viram inputs → Salvar/Cancelar. Cancelar descarta o rascunho e volta
 * ao modo leitura; Salvar manda SÓ o que mudou pro
 * PATCH /api/v1/clientes/:id/bloco/:bloco — o servidor grava a trilha campo a
 * campo (quem/quando/antes→depois) em cliente_historico.
 *
 * CNPJ/CPF não é editável aqui (tipo 'travado'): documento é a chave do
 * cadastro e só muda pelo fluxo próprio (correção/unificação de cadastros).
 * 🔴 #592 (19/10/2026): a exceção é o cadastro SEM documento — ali o campo vem como texto e o documento é PREENCHIDO uma vez.
 *
 * Testids: cli-edit-<bloco> · cli-save-<bloco> · cli-cancel-<bloco> ·
 * cli-campo-<bloco>-<name> (inputs do modo edição).
 */

import { useState, type ReactNode } from 'react';

import { Button, EmptyState, HelpTip, Input, Select, SelectOption, useToast } from '@/components/ui';
import { formatCurrency } from '@/lib/format';
// F4: o conversor mora no módulo de specs (arquivo de componente que exporta
// função quebra o fast-refresh — o gate barrou a versão exportada daqui).
import { converterRascunhoDeCampo } from '@/components/admin/clientes-blocos-spec';
import {
  useClienteTags,
  useClienteV2Actions,
  type ClienteBlocoSlug,
  type UpdateClienteBlocoInput,
} from '@/lib/clientes-v2-store';
// F2 (14/08): régua por etiqueta — asterisco e recusa falada na ficha.
import { useExigidosParaEtiquetas } from '@/lib/field-requirements-store';

export interface CampoBlocoSpec {
  /** Chave do campo no payload do PATCH (camelCase da API). */
  name: keyof UpdateClienteBlocoInput | 'documento';
  label: string;
  /**
   * 'texto' (default) · 'telefone' (só dígitos, 8-13) · 'numero' (inteiro ≥ 0)
   * · 'simnao' (tri-estado: —/Sim/Não) · 'travado' (nunca editável — CNPJ/CPF)
   * · 'moeda' (digita em reais, guarda em centavos).
   *
   * C-03: 'moeda' existe porque o limite de crédito é guardado em centavos.
   * Sem ele, quem digitasse "5000" pensando em R$ 5.000 gravaria R$ 50,00 —
   * erro silencioso, e num campo que trava faturamento.
   */
  // 'data' entrou com os campos de funcionário (mig 463): admissão, demissão e
  // aniversário. Guarda e trafega 'YYYY-MM-DD' — o `<input type="date">` já fala
  // esse formato —, e só a LEITURA vira DD/MM/AAAA. Sem hora de propósito: são
  // datas de calendário, não agendamentos (o padrão data+hora do CLAUDE.md vale
  // para agendamento de atividade, que é outra coisa).
  tipo?: 'texto' | 'telefone' | 'numero' | 'simnao' | 'travado' | 'moeda' | 'data';
  /** Fonte mono no modo leitura (inscrições, conta, Pix…). */
  mono?: boolean;
  /** Ocupa a linha inteira (fora do grid de colunas). */
  full?: boolean;
  /** Não pode ser salvo vazio (ex.: razão social). */
  obrigatorio?: boolean;
  /**
   * Limite de caracteres do input. C-08: a UF aceita 2 no servidor — sem o
   * limite aqui, quem digitasse "São Paulo" só descobriria o problema no 400.
   */
  maxLength?: number;
  /** Força maiúsculas enquanto digita (UF). */
  maiusculas?: boolean;
  /** Tooltip "?" ao lado do rótulo (usado no documento travado). */
  ajuda?: string;
  /**
   * Limite do Omie (15/08): contador "N/max" sempre visível na edição,
   * destacado quando passa. O valor NUNCA é truncado — exibir inteiro é
   * parte da regra; quem barra é o salvar (via `validarAoSalvar`).
   */
  contadorMax?: number;
  /**
   * Validação que roda no SALVAR, só quando o campo MUDOU (payload parcial):
   * devolve a mensagem de erro em português ou null. Formulário não é surdo —
   * a mensagem aparece via toast E o campo segue em edição.
   */
  validarAoSalvar?: (valor: string | number | boolean | null) => string | null;
  /**
   * C-3 — botão de AÇÃO ao lado do input, no modo edição.
   *
   * Nasceu do "Buscar pelo CEP", que existia no wizard e não na ficha: quem
   * corrigia um endereço errado do Omie tinha de digitar tudo à mão, enquanto
   * quem cadastrava de novo tinha o botão. A alternativa era remendar o bloco de
   * endereço com JSX próprio — e aí o de entrega ficaria diferente do fiscal.
   *
   * `onAcao` recebe o rascunho atual e devolve os campos a preencher; `null`
   * significa "não achei nada", e o chamador é quem avisa.
   */
  acao?: {
    /** Texto do botão. Verbo, não substantivo: "Buscar", não "Busca". */
    rotulo: string;
    /** Dica do que o botão faz — vira `title` e `aria-label`. */
    descricao: string;
    /** Roda a ação. Devolver `null` = não achou; o chamador avisa. */
    executar: (rascunhoAtual: string) => Promise<Record<string, string> | null>;
    /**
     * 🔴 19/10/2026 — o "Buscar dados pelo CNPJ" do documento vazio (#594). O CEP CORRIGE o endereço (sobrescreve); o CNPJ
     * só COMPLETA (o padrão do wizard e do "Completar pelo CNPJ": nunca passa por cima do que já foi digitado).
     */
    soCamposVazios?: boolean;
    /** Sem isto o botão liga com qualquer texto no campo. O CNPJ só liga com 14 dígitos válidos. */
    habilitada?: (rascunhoAtual: string) => boolean;
    /** As frases do aviso. Sem isto, as do endereço (o primeiro uso). */
    mensagens?: { preenchido: string; naoAchou: string; nadaAPreencher: string };
  };
  /** Formatação só de leitura (ex.: telefone com máscara). */
  formatLeitura?: (v: unknown) => string | null;
  /**
   * M-5: some do modo LEITURA, aparece na edição. Para campo cujo valor já está
   * dito por outro na mesma tela — hoje, "nome fantasia" quando é igual à razão
   * social. Quem decide é a tela, não este componente.
   */
  soEdicao?: boolean;
}

interface Props {
  clienteId: string;
  bloco: ClienteBlocoSlug;
  titulo: string;
  /** Tooltip "?" do bloco — explica o que é espelho do Omie e o que é só do v2. */
  ajuda: string;
  campos: CampoBlocoSpec[];
  /** Valores atuais do cliente, indexados pelo `name` de cada campo. */
  valores: Record<string, unknown>;
  podeEditar: boolean;
  /** Conteúdo extra no cabeçalho (ex.: selo "opcional"). */
  extraCabecalho?: ReactNode;
  /** Conteúdo abaixo dos campos (ex.: e-mails e segmento no bloco principal). */
  children?: ReactNode;
  /**
   * Campos (por `name`) que NÃO entram na linha "Sem preenchimento" — porta opcional (04/10, ficha do RH:
   * "Nome fantasia · Nº de unidades" não é assunto de funcionário). Preenchido, o campo continua aparecendo.
   */
  foraDoResumoVazio?: string[];
}

const soDigitos = (v: string) => v.replace(/\D/g, '');

/** Valor atual → texto do rascunho (bools viram ''/'sim'/'nao'). */
function paraRascunho(v: unknown, tipo: CampoBlocoSpec['tipo']): string {
  if (tipo === 'simnao') return v == null ? '' : v ? 'sim' : 'nao';
  // 'moeda': o banco guarda centavos, a pessoa edita em reais.
  if (tipo === 'moeda') return v == null ? '' : (Number(v) / 100).toFixed(2).replace('.', ',');
  return v == null ? '' : String(v);
}

function exibir(campo: CampoBlocoSpec, v: unknown): string | null {
  if (campo.formatLeitura) return campo.formatLeitura(v);
  if (campo.tipo === 'simnao') return v == null ? null : v ? 'Sim' : 'Não';
  if (campo.tipo === 'moeda') return v == null ? null : formatCurrency(Number(v) / 100);
  // 'data': 'YYYY-MM-DD' → 'DD/MM/AAAA'. Fatiado, não `new Date()`: construir um
  // Date a partir de 'YYYY-MM-DD' o interpreta como UTC e, em Brasília, mostra o
  // dia ANTERIOR. É o mesmo erro de fuso que o #76 custou uma rodada.
  if (campo.tipo === 'data') {
    if (v == null || v === '') return null;
    const [a, m, d] = String(v).slice(0, 10).split('-');
    return d && m && a ? `${d}/${m}/${a}` : String(v);
  }
  return v == null || v === '' ? null : String(v);
}

export function ClienteBlocoEditavel({
  clienteId,
  bloco,
  titulo,
  ajuda,
  campos,
  valores,
  podeEditar,
  extraCabecalho,
  children,
  foraDoResumoVazio,
}: Props) {
  const { updateBloco } = useClienteV2Actions();
  const { show } = useToast();
  const [editando, setEditando] = useState(false);
  // F2: a régua deste CADASTRO (união global ∪ etiquetas dele). O spec
  // continua mandando no obrigatório fixo (razão social); a config SOMA.
  const { data: tagsDoCliente = [] } = useClienteTags(clienteId);
  const exigidosDinamicos = useExigidosParaEtiquetas(tagsDoCliente.map((t: { tag: string }) => t.tag));
  const ehExigido = (c: CampoBlocoSpec) => Boolean(c.obrigatorio) || exigidosDinamicos.has(c.name);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});

  /** C-3: a ação de um campo preenche os outros. Devolve QUANTOS entraram — zero vira "nada a preencher", não "preenchido". */
  const preencherOutros = (campos: Record<string, string>, soVazios = false): number => {
    const aplicar = soVazios
      ? Object.fromEntries(Object.entries(campos).filter(([k]) => !(rascunho[k] ?? '').trim()))
      : campos;
    setRascunho((r) => ({ ...r, ...aplicar }));
    return Object.keys(aplicar).length;
  };

  const comecar = () => {
    const r: Record<string, string> = {};
    for (const c of campos) {
      if (c.tipo === 'travado') continue;
      r[c.name] = paraRascunho(valores[c.name], c.tipo);
    }
    setRascunho(r);
    setEditando(true);
  };

  const cancelar = () => {
    setEditando(false);
    setRascunho({});
  };

  /** Monta o payload SÓ com o que mudou; null = mensagem de erro já mostrada. */
  const montarPayload = (): UpdateClienteBlocoInput | null => {
    const payload: Record<string, unknown> = {};
    for (const c of campos) {
      if (c.tipo === 'travado') continue;
      const bruto = (rascunho[c.name] ?? '').trim();
      const original = valores[c.name] ?? null;
      const conv = converterRascunhoDeCampo(c, bruto);
      if (!conv.ok) {
        show(conv.erro, { variant: 'error' });
        return null;
      }
      const novo = conv.valor;
      if (c.tipo === 'telefone') {
        // Compara por dígitos pra não marcar "mudou" só por causa da máscara.
        if (soDigitos(original == null ? '' : String(original)) === (novo ?? '')) continue;
        payload[c.name] = novo;
        continue;
      }

      if (ehExigido(c) && novo == null) {
        show(`${c.label} não pode ficar em branco — é obrigatório pra este cadastro.`, { variant: 'error' });
        return null;
      }
      if (novo !== original) {
        // Limite do Omie (15/08): só cobra o que foi EDITADO — valor antigo
        // fora da regra não trava a edição dos outros campos.
        const erroValidacao = c.validarAoSalvar?.(novo);
        if (erroValidacao) {
          show(erroValidacao, { variant: 'error' });
          return null;
        }
        payload[c.name] = novo;
      }
    }
    return payload as UpdateClienteBlocoInput;
  };

  const salvar = () => {
    const payload = montarPayload();
    if (payload == null) return;
    if (Object.keys(payload).length === 0) {
      cancelar();
      return;
    }
    updateBloco.mutate(
      { id: clienteId, bloco, input: payload },
      {
        onSuccess: () => {
          cancelar();
          show('Alterações salvas.', { variant: 'success' });
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { message?: string } })?.response?.message ??
            'Não foi possível salvar as alterações. Tente de novo.';
          show(msg, { variant: 'error' });
        },
      },
    );
  };

  // M-5 (REALINHAR-MAIN): campo marcado com `soEdicao` some da LEITURA e volta
  // na edição. Nasceu para o caso "razão social e nome fantasia idênticos":
  // mostrar a mesma string duas vezes seguidas faz o leitor procurar a diferença
  // que não existe. Na edição os dois continuam lá — senão não haveria como
  // torná-los diferentes.
  const camposVisiveis = editando ? campos : campos.filter((c) => !c.soEdicao);

  // Campos full quebram o grid; consecutivos não-full compartilham um grid.
  const grupos: CampoBlocoSpec[][] = [];
  for (const c of camposVisiveis) {
    const ultimo = grupos[grupos.length - 1];
    if (c.full || !ultimo || ultimo[0].full) grupos.push([c]);
    else ultimo.push(c);
  }

  // FASE-C (maquete): no modo leitura o bloco mostra o que EXISTE — campo
  // vazio não vira "—"; os que faltam viram uma linha-resumo discreta. O modo
  // edição continua abrindo todos os campos.
  const estaVazio = (c: CampoBlocoSpec) => {
    const texto = exibir(c, valores[c.name]);
    return texto == null || texto === '';
  };
  const vazios = camposVisiveis.filter(estaVazio);
  const blocoVazio = vazios.length === camposVisiveis.length;
  const rotulosVazios = vazios.filter((c) => !(foraDoResumoVazio ?? []).includes(c.name)).map((c) => c.label);

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-3">
        <h3 className="font-semibold text-sm text-ink-1 dark:text-ink-dark-1">{titulo}</h3>
        <HelpTip texto={ajuda} />
        {extraCabecalho}
        <span className="ml-auto flex items-center gap-2">
          {podeEditar && !editando && (
            <Button variant="ghost" size="sm" onClick={comecar} data-testid={`cli-edit-${bloco}`}>
              Editar
            </Button>
          )}
          {editando && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelar}
                disabled={updateBloco.isPending}
                data-testid={`cli-cancel-${bloco}`}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={salvar}
                loading={updateBloco.isPending}
                data-testid={`cli-save-${bloco}`}
              >
                Salvar
              </Button>
            </>
          )}
        </span>
      </div>

      {/* M-5/A-3 (REALINHAR-MAIN): aqui a tela explicava a MECÂNICA — "o botão
          Editar abre todos os campos deste bloco". Instrução de onde clicar é
          sintoma de interface que não se explica sozinha; e quem já sabe clicar
          lê aquilo toda vez, de graça. Virou o `EmptyState` canônico, com a AÇÃO
          no lugar da explicação: "Preencher dados bancários" faz o que a frase
          descrevia, num clique só. */}
      {!editando && blocoVazio ? (
        <EmptyState
          // O título do bloco está logo acima; repeti-lo aqui ("Fiscal e
          // inscrições" / "Sem fiscal e inscrições") só ecoa. Quem carrega o
          // nome é a AÇÃO.
          title="Nada preenchido"
          tamanho="compacto"
          action={
            podeEditar ? (
              <Button variant="secondary" size="sm" onClick={comecar} data-testid={`cli-preencher-${bloco}`}>
                {/* 04/10: só a PRIMEIRA letra vira minúscula — "Dados de RH" virava "dados de rh". Para os
                    outros títulos ("Dados bancários", "Fiscal e inscrições"…) o resultado é o mesmo de antes. */}
                Preencher {titulo.charAt(0).toLocaleLowerCase('pt-BR') + titulo.slice(1)}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {grupos.map((grupo, gi) =>
            grupo[0].full ? (
              <CampoCelula
                key={grupo[0].name}
                campo={grupo[0]}
                bloco={bloco}
                exigido={ehExigido(grupo[0])}
                editando={editando}
                valor={valores[grupo[0].name]}
                rascunho={rascunho[grupo[0].name] ?? ''}
                onChange={(v) => setRascunho((r) => ({ ...r, [grupo[0].name]: v }))}
                onPreencherOutros={preencherOutros}
              />
            ) : (
              <div key={`grid-${gi}`} className="grid grid-cols-2 md:grid-cols-3 gap-x-4">
                {grupo.map((c) => (
                  <CampoCelula
                    key={c.name}
                    campo={c}
                    bloco={bloco}
                    exigido={ehExigido(c)}
                    editando={editando}
                    valor={valores[c.name]}
                    rascunho={rascunho[c.name] ?? ''}
                    onChange={(v) => setRascunho((r) => ({ ...r, [c.name]: v }))}
                    onPreencherOutros={preencherOutros}
                  />
                ))}
              </div>
            ),
          )}
          {!editando && rotulosVazios.length > 0 && (
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1 mb-2">
              Sem preenchimento: {rotulosVazios.join(' · ')}
            </p>
          )}
        </>
      )}

      {children}
    </div>
  );
}

// F3: exportado — o wizard dinâmico (F4) renderiza o MESMO campo, sem o
// chrome do card da ficha. Campo copiado é o desenho errado.
export function CampoCelula({
  campo,
  bloco,
  exigido = false,
  editando,
  valor,
  rascunho,
  onChange,
  onPreencherOutros,
}: {
  campo: CampoBlocoSpec;
  bloco: string;
  /** F2: exigido pela régua por etiqueta (config) OU pelo spec fixo. */
  exigido?: boolean;
  editando: boolean;
  valor: unknown;
  rascunho: string;
  onChange: (v: string) => void;
  /** C-3: a ação preenche OUTROS campos do mesmo bloco (CEP → rua, bairro, …). Pode devolver quantos entraram. */
  onPreencherOutros: (campos: Record<string, string>, soVazios?: boolean) => number | void;
}) {
  const rotulo = (
    <div className="text-xs font-medium text-ink-3 dark:text-ink-dark-3 mb-0.5 inline-flex items-center gap-1">
      {campo.label}
      {exigido && <span className="text-red" aria-hidden="true">*</span>}
      {editando && campo.ajuda && <HelpTip texto={campo.ajuda} />}
    </div>
  );

  if (!editando) {
    const texto = exibir(campo, valor);
    // FASE-C: campo vazio não renderiza "—" — quem lista o que falta é a
    // linha-resumo "Sem preenchimento" do bloco.
    if (texto == null || texto === '') return null;
    return (
      <div className="mb-3">
        {rotulo}
        <div
          className={
            campo.mono
              ? 'text-sm font-mono text-ink-1 dark:text-ink-dark-1'
              : 'text-sm text-ink-1 dark:text-ink-dark-1'
          }
        >
          {texto}
        </div>
      </div>
    );
  }

  // ── Modo edição ──────────────────────────────────────────────────────────
  if (campo.tipo === 'travado') {
    return (
      <div className="mb-3">
        {rotulo}
        <Input
          size="sm"
          disabled
          value={exibir(campo, valor) ?? '—'}
          aria-label={campo.label}
          data-testid={`cli-campo-${bloco}-${campo.name}`}
        />
      </div>
    );
  }

  if (campo.tipo === 'simnao') {
    return (
      <div className="mb-3">
        {rotulo}
        <Select
          size="sm"
          value={rascunho}
          onChange={(e) => onChange(e.target.value)}
          aria-label={campo.label}
          data-testid={`cli-campo-${bloco}-${campo.name}`}
        >
          <SelectOption value="">—</SelectOption>
          <SelectOption value="sim">Sim</SelectOption>
          <SelectOption value="nao">Não</SelectOption>
        </Select>
      </div>
    );
  }

  const input = (
    <Input
      size="sm"
      type={campo.tipo === 'numero' ? 'number' : campo.tipo === 'data' ? 'date' : 'text'}
      min={campo.tipo === 'numero' ? 0 : undefined}
      maxLength={campo.maxLength}
      inputMode={campo.tipo === 'telefone' ? 'tel' : undefined}
      value={rascunho}
      onChange={(e) =>
        onChange(campo.maiusculas ? e.target.value.toUpperCase() : e.target.value)
      }
      aria-label={campo.label}
      data-testid={`cli-campo-${bloco}-${campo.name}`}
    />
  );

  return (
    <div className="mb-3">
      {rotulo}
      {campo.acao ? (
        // C-3: input + botão na mesma linha. `items-stretch` pra o botão ter a
        // altura do input — desalinhado por 2px é o tipo de coisa que ninguém
        // reporta e todo mundo vê.
        <div className="flex items-stretch gap-2">
          <div className="min-w-0 flex-1">{input}</div>
          <BotaoAcaoCampo
            campo={campo}
            bloco={bloco}
            rascunho={rascunho}
            onPreencher={onPreencherOutros}
          />
        </div>
      ) : (
        input
      )}
      {campo.contadorMax !== undefined && (
        // Limite do Omie (15/08): contador sempre visível na edição; o valor
        // nunca é truncado — quem barra é o salvar (validarAoSalvar).
        <p
          className={`text-xs mt-0.5 ${rascunho.trim().length > campo.contadorMax ? 'text-danger font-medium' : 'text-ink-3 dark:text-ink-dark-3'}`}
          data-testid={`cli-contador-${bloco}-${campo.name}`}
        >
          {rascunho.trim().length}/{campo.contadorMax}
        </p>
      )}
    </div>
  );
}

/**
 * O botão de ação de um campo (C-3).
 *
 * Estado próprio pra não re-renderizar o bloco inteiro a cada clique, e porque
 * "buscando…" é estado dele, não do formulário.
 */
function BotaoAcaoCampo({
  campo,
  bloco,
  rascunho,
  onPreencher,
}: {
  campo: CampoBlocoSpec;
  bloco: string;
  rascunho: string;
  onPreencher: (campos: Record<string, string>, soVazios?: boolean) => number | void;
}) {
  const [carregando, setCarregando] = useState(false);
  const toast = useToast();
  const mensagens = campo.acao?.mensagens ?? {
    preenchido: 'Endereço preenchido. Confira antes de salvar.',
    naoAchou: 'Não encontramos esse endereço. Confira o número ou preencha à mão.',
    nadaAPreencher: 'Os campos do endereço já estão preenchidos.',
  };
  const habilitada = campo.acao?.habilitada ? campo.acao.habilitada(rascunho) : !!rascunho.trim();

  async function clicar() {
    if (!campo.acao || carregando) return;
    setCarregando(true);
    try {
      const preencher = await campo.acao.executar(rascunho);
      if (!preencher) {
        // Formulário nunca é surdo (regra 15): "não achei" é resposta, e a
        // pessoa precisa dela pra decidir se digita à mão.
        toast.show(mensagens.naoAchou, { variant: 'info' });
        return;
      }
      const entraram = onPreencher(preencher, campo.acao.soCamposVazios);
      if (entraram === 0) {
        toast.show(mensagens.nadaAPreencher, { variant: 'info' });
        return;
      }
      toast.show(mensagens.preenchido, { variant: 'success' });
    } catch (err) {
      // A frase do servidor diz o que houve ("CNPJ não encontrado na Receita…") — a genérica só quando ele não disse nada.
      const doServidor = (err as { response?: { message?: string } })?.response?.message;
      toast.show(doServidor ?? 'A consulta falhou. Tente de novo em instantes.', { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={clicar}
      disabled={carregando || !habilitada}
      title={campo.acao?.descricao}
      aria-label={campo.acao?.descricao}
      data-testid={`cli-acao-${bloco}-${campo.name}`}
    >
      {carregando ? 'Buscando…' : campo.acao?.rotulo}
    </Button>
  );
}
